import { useState, useEffect } from 'react';
import { Lock, Users, Mail, FileText, LogOut, RefreshCw, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [storedPassword, setStoredPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/login`, { password });
      setStoredPassword(password);
      setIsAuthenticated(true);
      fetchData(password);
    } catch (err) {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (pwd) => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${pwd}` },
      });
      setData(response.data);
    } catch (err) {
      setError('Failed to fetch data');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setStoredPassword('');
    setData(null);
  };

  const handleRefresh = () => {
    fetchData(storedPassword);
  };

  if (!isAuthenticated) {
    return (
      <main className="pt-20 min-h-screen bg-[#F8F9FA]" data-testid="admin-login">
        <div className="max-w-md mx-auto px-6 py-20">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-slate-200">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#2C3E50] rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-white" size={28} />
              </div>
              <h1 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
                Admin Login
              </h1>
              <p className="text-[#64748B] text-sm mt-2">
                Enter your admin password to continue
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-slate-200 focus:border-[#3498DB]"
                data-testid="admin-password-input"
              />
              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#2C3E50] hover:bg-[#1a252f] text-white"
                data-testid="admin-login-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
              Admin Dashboard
            </h1>
            <p className="text-[#64748B] text-sm">Manage submissions and leads</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleRefresh}
              variant="outline"
              className="border-[#2C3E50] text-[#2C3E50]"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Stats */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#3498DB]/10 rounded-lg flex items-center justify-center">
                  <Mail className="text-[#3498DB]" size={24} />
                </div>
                <div>
                  <p className="text-[#64748B] text-sm">Contact Forms</p>
                  <p className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
                    {data.stats.total_contacts}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#27AE60]/10 rounded-lg flex items-center justify-center">
                  <Users className="text-[#27AE60]" size={24} />
                </div>
                <div>
                  <p className="text-[#64748B] text-sm">Newsletter Subscribers</p>
                  <p className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
                    {data.stats.total_subscribers}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#9B59B6]/10 rounded-lg flex items-center justify-center">
                  <FileText className="text-[#9B59B6]" size={24} />
                </div>
                <div>
                  <p className="text-[#64748B] text-sm">Whitepaper Leads</p>
                  <p className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
                    {data.stats.total_leads}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#E67E22]/10 rounded-lg flex items-center justify-center">
                  <Zap className="text-[#E67E22]" size={24} />
                </div>
                <div>
                  <p className="text-[#64748B] text-sm">Protocol Checker Leads</p>
                  <p className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50]">
                    {data.stats.total_checker_leads ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['contacts', 'newsletter', 'leads', 'protocol'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'bg-[#2C3E50] text-white'
                  : 'bg-white text-[#64748B] hover:bg-slate-100'
              }`}
            >
              {tab === 'protocol' ? 'Protocol Leads' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Data Table */}
        {data && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              {activeTab === 'contacts' && (
                <table className="w-full">
                  <thead className="bg-[#F8F9FA] border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Name</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Email</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Company</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Type</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Message</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contacts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[#64748B]">
                          No contact submissions yet
                        </td>
                      </tr>
                    ) : (
                      data.contacts.map((contact, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4 text-[#2C3E50]">{contact.name}</td>
                          <td className="p-4 text-[#3498DB]">{contact.email}</td>
                          <td className="p-4 text-[#64748B]">{contact.company || '-'}</td>
                          <td className="p-4">
                            <span className="px-2 py-1 bg-[#3498DB]/10 text-[#3498DB] text-xs rounded-full">
                              {contact.inquiry_type}
                            </span>
                          </td>
                          <td className="p-4 text-[#64748B] max-w-xs truncate">{contact.message}</td>
                          <td className="p-4 text-[#64748B] text-sm">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'newsletter' && (
                <table className="w-full">
                  <thead className="bg-[#F8F9FA] border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Email</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Status</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Subscribed Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.newsletter.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-8 text-center text-[#64748B]">
                          No newsletter subscribers yet
                        </td>
                      </tr>
                    ) : (
                      data.newsletter.map((sub, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4 text-[#3498DB]">{sub.email}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              sub.is_active 
                                ? 'bg-[#27AE60]/10 text-[#27AE60]' 
                                : 'bg-red-100 text-red-500'
                            }`}>
                              {sub.is_active ? 'Active' : 'Unsubscribed'}
                            </span>
                          </td>
                          <td className="p-4 text-[#64748B] text-sm">
                            {new Date(sub.subscribed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {activeTab === 'leads' && (
                <table className="w-full">
                  <thead className="bg-[#F8F9FA] border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Name</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Email</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Source</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Whitepaper</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leads.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-[#64748B]">
                          No leads yet
                        </td>
                      </tr>
                    ) : (
                      data.leads.map((lead, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4 text-[#2C3E50]">{lead.name}</td>
                          <td className="p-4 text-[#3498DB]">{lead.email}</td>
                          <td className="p-4">
                            <span className="px-2 py-1 bg-[#9B59B6]/10 text-[#9B59B6] text-xs rounded-full">
                              {lead.source}
                            </span>
                          </td>
                          <td className="p-4 text-[#64748B]">{lead.whitepaper_id || '-'}</td>
                          <td className="p-4 text-[#64748B] text-sm">
                            {new Date(lead.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
              {activeTab === 'protocol' && (
                <table className="w-full">
                  <thead className="bg-[#F8F9FA] border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Email</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">URL Checked</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Platform</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">ACP</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">UCP</th>
                      <th className="text-left p-4 font-semibold text-[#2C3E50]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.checker_leads ?? []).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-[#64748B]">
                          No protocol checker leads yet
                        </td>
                      </tr>
                    ) : (
                      (data.checker_leads ?? []).map((lead, idx) => {
                        const acp = lead.acp?.score ?? '—';
                        const ucp = lead.ucp?.score ?? '—';
                        const scoreColor = (s) => {
                          if (s >= 67) return 'bg-[#27AE60]/10 text-[#27AE60]';
                          if (s >= 33) return 'bg-[#F39C12]/10 text-[#F39C12]';
                          return 'bg-red-100 text-red-500';
                        };
                        return (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-4 text-[#3498DB]">{lead.email}</td>
                            <td className="p-4 text-[#64748B] max-w-[180px] truncate text-sm" title={lead.url}>{lead.url}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-[#E67E22]/10 text-[#E67E22] text-xs rounded-full capitalize">
                                {lead.platform ?? '—'}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${scoreColor(acp)}`}>
                                {acp}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${scoreColor(ucp)}`}>
                                {ucp}
                              </span>
                            </td>
                            <td className="p-4 text-[#64748B] text-sm">
                              {lead.submitted_at ? new Date(lead.submitted_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
